import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { buildAvatarImageSource } from '../../../utils/buildAvatarImageSource';
import { pluralize } from '../../../utils/pluralize';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { useIsCollectionReported } from '../../hooks/useIsCollectionReported';
import { IconKebab } from '../../icon/IconKebab';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Avatar } from '../../media/Avatar';
import { MediaCover } from '../../media/MediaCover';
import { CollectionCollage } from '../CollectionCollage/CollectionCollage';
import { makeMediaParams } from '../PostDetailsCard/utils';
import { getLinkProps } from '../utils/getLinkProps';
import { AVATAR_SIZE, MENU_TRIGGER_SIZE, POST_COLLECTION_GRID_CARD_RATIO } from './constants';
import { ReportedPost } from './parts/ReportedPost/ReportedPost';
import { ViewsBadge } from './parts/ViewsBadge/ViewsBadge';
import { BusinessName, CardFooter, CollectionBadgesWrapper, PostsNumber, Title } from './styles';
import { PostCollectionGridCardProps } from './types';
import { usePostGridCardKebab } from './usePostGridCardKebab';

export const PostCollectionCardGrid: FC<PostCollectionGridCardProps> = ({
    post,
    index,
    onReport,
    onGridRowShown,
    gridColumns,
    isVisible = false,
    onShareCollection,
    onContextMenuOpen,
    onSaveCollection,
    onRemoveSavedCollection,
    onVisibilityChange,
    onHover,
    onHoverCancel,
    onCollectionGridCardClick,
    onReaction,
    hideReportedPosts = true,
    imageAlias,
    innerRef,
}) => {
    const t = useTranslation();
    const isReported = useIsCollectionReported(post.collectionData?.id || '');
    const isFirstRenderRef = useRef(true);

    const isViewsBadgeEnabled = Number(useExperimentFlag('show_views_badge')) === 1;

    const postNumber = useMemo(() => {
        if (!post.collectionData) {
            return;
        }

        return t(
            `${post.collectionData.numberOfPosts} ${pluralize(
                post.collectionData.numberOfPosts,
                'пост',
                'поста',
                'постов',
            )}`,
        );
    }, [post.collectionData, t]);

    const handleReport = useCallback(() => {
        onReport?.();
    }, [onReport]);

    const { kebab, openKebab } = usePostGridCardKebab({
        onReport: handleReport,
        onShareCollection,
        onSaveCollection,
        onRemoveSavedCollection,
        onReaction,
        post,
    });

    const handleOpenKebab = useCallback(
        (e: React.MouseEvent) => {
            openKebab(e);
            onContextMenuOpen?.();
        },
        [openKebab, onContextMenuOpen],
    );

    const handleClick = useCallback(() => {
        onCollectionGridCardClick({ postNumber: index });
    }, [onCollectionGridCardClick, index]);

    // Хак, чтобы вызывать onGridRowShown при первом рендере
    // isVisible обновится только после скролла
    useEffect(() => {
        if (index + 1 === gridColumns) {
            onGridRowShown?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onGridRowShown, gridColumns]);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;

            return;
        }

        if (isVisible && (index + 1) % gridColumns === 0) {
            onGridRowShown?.();
        }

        onVisibilityChange({
            ...makeMediaParams(post, 0),
            isInViewport: isVisible,
            postNumber: index,
            isGrid: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, onGridRowShown, index, gridColumns, onVisibilityChange]);

    if (isReported && hideReportedPosts) {
        return <ReportedPost text="Коллекция скрыта из вашей ленты" />;
    }

    return (
        <Box position="relative" onMouseEnter={onHover} onMouseLeave={onHoverCancel} ref={innerRef}>
            <MediaCover
                width="100%"
                height="100%"
                ratio={POST_COLLECTION_GRID_CARD_RATIO}
                onClick={handleClick}
                cursor="pointer"
                borderRadius={2}
            >
                <Flex alignItems="center" justifyContent="center">
                    <CollectionCollage
                        content={post.content}
                        index={index}
                        ratio={POST_COLLECTION_GRID_CARD_RATIO}
                        imageAlias={imageAlias}
                    />
                </Flex>
                {post.publicStats?.shows || post.collectionData?.title ? (
                    <CollectionBadgesWrapper>
                        {isViewsBadgeEnabled && post.publicStats?.shows ? (
                            <ViewsBadge views={post.publicStats.shows} />
                        ) : null}
                        {post.collectionData?.title ? (
                            <Title>{post.collectionData?.title}</Title>
                        ) : null}
                    </CollectionBadgesWrapper>
                ) : null}
                {postNumber ? <PostsNumber>{postNumber}</PostsNumber> : null}
            </MediaCover>
            {kebab()}
            <CardFooter>
                <Flex
                    direction="row"
                    alignItems="center"
                    width={`calc(100% - ${MENU_TRIGGER_SIZE}px)`}
                    spacing={1}
                    {...getLinkProps(post.ownersData?.author?.shareLink)}
                >
                    <Avatar
                        width={AVATAR_SIZE}
                        height={AVATAR_SIZE}
                        title={post.ownersData?.author?.displayName}
                        src={buildAvatarImageSource(post.ownersData?.author?.avatarUrl)}
                        border="0.5px solid var(--divider)"
                    />
                    <BusinessName>{post.ownersData?.author?.displayName || ''}</BusinessName>
                </Flex>
                <Box onClick={handleOpenKebab} cursor="pointer">
                    <IconKebab
                        color="var(--color-primary)"
                        fontSize={MENU_TRIGGER_SIZE}
                        dataTestId={DataTestId.POST_COLLECTION_CARD_KEBAB}
                    />
                </Box>
            </CardFooter>
        </Box>
    );
};
